import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { requireAdminGroup } from './permissions';

// Admin-SPA routes регистрируются безусловно — build-time FE-флаги
// исключены намеренно.  Все gating'и админ-секций живут BE-side
// (parенные `*_ENABLED` settings + permission-группы IsInAdminGroup).
// Дублирующий FE-флаг не добавляет безопасности (BE сам отдаёт 404 от
// disabled endpoint'а), но создаёт риск молчаливого разъезда между
// rebuild'ом FE-бандла и env-переменными.  Если нужен runtime-toggle —
// делать через /api/admin-api/me/ response, а не через build-time env.
const genericRoutes: RouteRecordRaw[] = [
    {
        path: '_models/:modelLabel',
        name: 'admin-generic-list',
        component: () => import('@/admin/generic/components/AdminCrudListPage.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            pageTitle: 'Список — админ',
            breadcrumbs: (route: RouteLocationNormalized) => [
                { label: 'Все модели' },
                { label: String(route.params.modelLabel) },
            ],
        },
        beforeEnter: requireAdminGroup,
    },
    {
        path: '_models/:modelLabel/new',
        name: 'admin-generic-create',
        component: () => import('@/admin/generic/components/AdminCrudCreatePage.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            pageTitle: 'Создать — админ',
            breadcrumbs: (route: RouteLocationNormalized) => [
                { label: 'Все модели' },
                {
                    label: String(route.params.modelLabel),
                    to: {
                        name: 'admin-generic-list',
                        params: { modelLabel: String(route.params.modelLabel) },
                    },
                },
                { label: 'Создать' },
            ],
        },
        beforeEnter: requireAdminGroup,
    },
    {
        path: '_models/:modelLabel/:pk',
        name: 'admin-generic-detail',
        component: () => import('@/admin/generic/components/AdminCrudDetailPage.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            pageTitle: 'Детали — админ',
            breadcrumbs: (route: RouteLocationNormalized) => [
                { label: 'Все модели' },
                {
                    label: String(route.params.modelLabel),
                    to: {
                        name: 'admin-generic-list',
                        params: { modelLabel: String(route.params.modelLabel) },
                    },
                },
                { label: `#${route.params.pk}` },
            ],
        },
        beforeEnter: requireAdminGroup,
    },
    {
        path: '_models/:modelLabel/:pk/edit',
        name: 'admin-generic-edit',
        component: () => import('@/admin/generic/components/AdminCrudEditPage.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            pageTitle: 'Редактирование — админ',
            breadcrumbs: (route: RouteLocationNormalized) => [
                { label: 'Все модели' },
                {
                    label: String(route.params.modelLabel),
                    to: {
                        name: 'admin-generic-list',
                        params: { modelLabel: String(route.params.modelLabel) },
                    },
                },
                { label: `Редактировать #${route.params.pk}` },
            ],
        },
        beforeEnter: requireAdminGroup,
    },
];

const supportRoutes: RouteRecordRaw[] = [
    {
        path: 'support',
        meta: {
            requiredAdminGroup: 'admin.support',
        },
        children: [
            {
                path: '',
                name: 'admin-support-queue',
                component: () => import('@/admin/pages/support/SupportQueuePage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.support',
                    pageTitle: 'Очередь обращений — админ',
                    breadcrumbs: () => [
                        { label: 'Поддержка', to: { name: 'admin-support-queue' } },
                        { label: 'Очередь' },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                path: 'tickets/:publicId',
                name: 'admin-support-ticket',
                component: () => import('@/admin/pages/support/SupportTicketPage.vue'),
                props: true,
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.support',
                    pageTitle: 'Тикет — админ',
                    breadcrumbs: (route: RouteLocationNormalized) => [
                        { label: 'Поддержка', to: { name: 'admin-support-queue' } },
                        { label: `Тикет #${route.params.publicId}` },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
        ],
    },
];

const mentorRoutes: RouteRecordRaw[] = [
    {
        path: 'mentors',
        meta: { requiredAdminGroup: 'admin.mentors' },
        children: [
            {
                path: '',
                name: 'admin-mentors-list',
                component: () => import('@/admin/pages/mentors/MentorsListPage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.mentors',
                    pageTitle: 'Наставники — админ',
                    breadcrumbs: () => [{ label: 'Наставники' }],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                path: 'new',
                name: 'admin-mentor-create',
                component: () => import('@/admin/pages/mentors/MentorCreatePage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.mentors',
                    pageTitle: 'Новый наставник',
                    breadcrumbs: () => [
                        { label: 'Наставники', to: { name: 'admin-mentors-list' } },
                        { label: 'Новый наставник' },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                // Was :id(\d+) for the legacy auto-increment PK; UserProfile.id
                // is a UUID after the May 2026 schema regeneration, so the
                // path now accepts any non-slash slug.
                path: ':id',
                name: 'admin-mentor-detail',
                component: () => import('@/admin/pages/mentors/MentorDetailPage.vue'),
                props: true,
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.mentors',
                    pageTitle: 'Наставник — админ',
                    breadcrumbs: (route: RouteLocationNormalized) => [
                        { label: 'Наставники', to: { name: 'admin-mentors-list' } },
                        { label: `Наставник #${route.params.id}` },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
        ],
    },
    {
        path: 'moderation',
        name: 'admin-moderation',
        component: () => import('@/admin/pages/moderation/ModerationQueuePage.vue'),
        meta: {
            requiresAuth: true,
            requiredAdminGroup: 'admin.mentors',
            pageTitle: 'Модерация mentor-claim',
            breadcrumbs: () => [{ label: 'Модерация' }],
        },
        beforeEnter: requireAdminGroup,
    },
    {
        path: 'mentor-applications',
        meta: { requiredAdminGroup: 'admin.mentors' },
        children: [
            {
                path: '',
                name: 'admin-mentor-applications-list',
                component: () =>
                    import('@/admin/pages/mentor-applications/MentorApplicationsListPage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.mentors',
                    pageTitle: 'Заявки на наставника — админ',
                    breadcrumbs: () => [{ label: 'Заявки на наставника' }],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                // Was :id(\d+) for the legacy auto-increment PK; MentorApplication.id
                // is a UUID after the May 2026 schema regeneration.
                path: ':id',
                name: 'admin-mentor-application-detail',
                component: () =>
                    import('@/admin/pages/mentor-applications/MentorApplicationDetailPage.vue'),
                props: true,
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.mentors',
                    pageTitle: 'Заявка — админ',
                    breadcrumbs: (route: RouteLocationNormalized) => [
                        {
                            label: 'Заявки на наставника',
                            to: { name: 'admin-mentor-applications-list' },
                        },
                        { label: `Заявка #${route.params.id}` },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
        ],
    },
];

const financeRoutes: RouteRecordRaw[] = [
    {
        path: 'deals',
        meta: { requiredAdminGroup: 'admin.finance' },
        children: [
            {
                path: '',
                name: 'admin-deals-list',
                component: () => import('@/admin/pages/deals/DealsListPage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.finance',
                    pageTitle: 'Платежи — админ',
                    breadcrumbs: () => [{ label: 'Платежи' }],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                path: ':publicId',
                name: 'admin-deal-detail',
                component: () => import('@/admin/pages/deals/DealDetailPage.vue'),
                props: true,
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.finance',
                    pageTitle: 'Сделка — админ',
                    breadcrumbs: (route: RouteLocationNormalized) => [
                        { label: 'Платежи', to: { name: 'admin-deals-list' } },
                        { label: `Сделка ${route.params.publicId}` },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
        ],
    },
    {
        path: 'payment-events',
        name: 'admin-payment-events',
        component: () => import('@/admin/pages/payment-events/PaymentEventsPage.vue'),
        meta: {
            requiresAuth: true,
            requiredAdminGroup: 'admin.finance',
            pageTitle: 'Webhook-события — админ',
            breadcrumbs: () => [{ label: 'Webhook-события' }],
        },
        beforeEnter: requireAdminGroup,
    },
    {
        path: 'tbank-calls',
        name: 'admin-tbank-calls-list',
        component: () => import('@/admin/pages/tbank-calls/TBankCallsListPage.vue'),
        meta: {
            requiresAuth: true,
            requiredAdminGroup: 'admin.finance',
            pageTitle: 'T-API логи — админ',
            breadcrumbs: () => [{ label: 'T-API логи' }],
        },
        beforeEnter: requireAdminGroup,
    },
    {
        path: 'tbank-calls/:id',
        name: 'admin-tbank-call-detail',
        component: () => import('@/admin/pages/tbank-calls/TBankCallDetailPage.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            requiredAdminGroup: 'admin.finance',
            pageTitle: 'T-API вызов — админ',
            breadcrumbs: (route: RouteLocationNormalized) => [
                { label: 'T-API логи', to: { name: 'admin-tbank-calls-list' } },
                { label: `#${route.params.id}` },
            ],
        },
        beforeEnter: requireAdminGroup,
    },
];

const disputeRoutes: RouteRecordRaw[] = [
    {
        path: 'disputes/redirect/:publicId',
        name: 'admin-dispute-redirector',
        component: () => import('@/admin/pages/disputes/DisputeRedirectorPage.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            requiredAdminGroup: 'admin.support',
            pageTitle: 'Спор — переход',
            breadcrumbs: () => [{ label: 'Спор' }],
        },
        beforeEnter: requireAdminGroup,
    },
];

const paymentLifecycleRoutes: RouteRecordRaw[] = [
    {
        path: 'payment-lifecycle',
        name: 'admin-payment-lifecycle',
        component: () => import('@/admin/pages/payment-lifecycle/PaymentLifecyclePage.vue'),
        meta: {
            requiresAuth: true,
            requiredAdminGroup: 'admin.finance',
            pageTitle: 'Жизненный цикл — админ',
            breadcrumbs: () => [{ label: 'Жизненный цикл' }],
        },
        beforeEnter: requireAdminGroup,
    },
];

const founderInviteRoutes: RouteRecordRaw[] = [
    {
        path: 'founder-invites',
        meta: { requiredAdminGroup: 'admin.conferences' },
        children: [
            {
                path: '',
                name: 'admin-founder-invites-list',
                component: () => import('@/admin/pages/founder-invites/FounderInvitesListPage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.conferences',
                    pageTitle: 'Приглашения на founder-call — админ',
                    breadcrumbs: () => [{ label: 'Приглашения на founder-call' }],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                path: 'new',
                name: 'admin-founder-invites-create',
                component: () =>
                    import('@/admin/pages/founder-invites/FounderInvitesCreatePage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.conferences',
                    pageTitle: 'Новое приглашение',
                    breadcrumbs: () => [
                        {
                            label: 'Приглашения на founder-call',
                            to: { name: 'admin-founder-invites-list' },
                        },
                        { label: 'Новое' },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
            {
                path: ':id(\\d+)',
                name: 'admin-founder-invites-detail',
                component: () =>
                    import('@/admin/pages/founder-invites/FounderInvitesDetailPage.vue'),
                props: true,
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.conferences',
                    pageTitle: 'Приглашение — админ',
                    breadcrumbs: (route: RouteLocationNormalized) => [
                        {
                            label: 'Приглашения на founder-call',
                            to: { name: 'admin-founder-invites-list' },
                        },
                        { label: `#${route.params.id}` },
                    ],
                },
                beforeEnter: requireAdminGroup,
            },
        ],
    },
];

export const adminRoutes: RouteRecordRaw[] = [
    {
        path: '/admin',
        component: () => import('@/admin/app/AdminApp.vue'),
        // hideAppChrome scrubs the public-site header / left sidebar / footer
        // so the AdminApp shell occupies the full viewport. Vue Router 4
        // merges parent meta into route.meta, so all /admin/* children
        // inherit this automatically.
        meta: { requiresAuth: true, pageTitle: 'Админ-панель', hideAppChrome: true },
        beforeEnter: requireAdminGroup,
        children: [
            {
                path: '',
                name: 'admin-home',
                redirect: { name: 'admin-dashboard' },
            },
            {
                path: 'dashboard',
                name: 'admin-dashboard',
                component: () => import('@/admin/pages/DashboardPage.vue'),
                meta: {
                    requiresAuth: true,
                    requiredAdminGroup: 'admin.dashboard',
                    pageTitle: 'Дашборд — админ',
                    breadcrumbs: () => [{ label: 'Дашборд' }],
                },
                beforeEnter: requireAdminGroup,
            },
            ...supportRoutes,
            ...mentorRoutes,
            ...financeRoutes,
            ...disputeRoutes,
            ...paymentLifecycleRoutes,
            ...founderInviteRoutes,
            ...genericRoutes,
        ],
    },
];
